import { CheckCircle2, Globe, Plug, Plus, Save, Trash2, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Page, PageHeader } from '../components/layout/AppShell';
import { ConfirmDialog, useToast } from '../components/ui/overlays';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Field,
  Input,
  LoadingBlock,
  Select,
  Switch,
  Textarea,
} from '../components/ui/primitives';
import {
  useContentChannels,
  useDeleteContentChannel,
  useSaveContentChannel,
  useSession,
  useTestContentChannel,
} from '../hooks/queries';
import { formatRelative } from '../lib/utils';
import type { ContentChannel, ContentChannelTestResult } from '../types/api';

const TIMEZONES = [
  'Europe/Brussels',
  'Europe/Amsterdam',
  'Europe/Berlin',
  'Europe/London',
  'Europe/Paris',
  'Europe/Madrid',
  'Europe/Oslo',
  'Europe/Warsaw',
  'America/New_York',
  'America/Los_Angeles',
  'UTC',
];

const DAYS = [
  ['1', 'Mon'],
  ['2', 'Tue'],
  ['3', 'Wed'],
  ['4', 'Thu'],
  ['5', 'Fri'],
  ['6', 'Sat'],
  ['7', 'Sun'],
] as const;

const BLANK = {
  label: 'Blog',
  provider: 'GHOST' as const,
  ghostAdminUrl: '',
  ghostAdminKey: '',
  pexelsApiKey: '',
  unsplashApiKey: '',
  siteUrl: '',
  defaultPostStatus: 'draft' as 'draft' | 'published',
  embedSnippet: '',
  defaultTags: '',
  minSeoScore: 70,
  language: '',
  dailyLimit: 3,
  publishWindowStart: 8,
  publishWindowEnd: 18,
  publishDays: '1,2,3,4,5',
  timezone: 'Europe/Brussels',
  isDefault: true,
  isActive: true,
};

type Draft = typeof BLANK & {
  id?: string;
  hasGhostKey?: boolean;
  hasPexelsKey?: boolean;
  hasUnsplashKey?: boolean;
};

export const ContentChannelPage = () => {
  const { projectId = '' } = useParams<{ projectId: string }>();
  const { data: channels, isLoading } = useContentChannels(projectId);
  const { data: session } = useSession();
  const saveChannel = useSaveContentChannel(projectId);
  const deleteChannel = useDeleteContentChannel(projectId);
  const testChannel = useTestContentChannel();
  const toast = useToast();

  const [draft, setDraft] = useState<Draft | null>(null);
  const [testResult, setTestResult] = useState<ContentChannelTestResult | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ContentChannel | null>(null);

  // Open the first channel for editing as soon as it loads: this page is almost always
  // "edit the one channel", not "browse a list".
  useEffect(() => {
    if (!channels || draft) return;
    const first = channels[0];
    if (first) setDraft(toDraft(first));
  }, [channels, draft]);

  const save = async () => {
    if (!draft) return;
    try {
      const payload: Record<string, unknown> = { ...draft };
      // An empty key field means "keep the stored key".
      if (!draft.ghostAdminKey) delete payload.ghostAdminKey;
      if (!draft.pexelsApiKey) delete payload.pexelsApiKey;
      if (!draft.unsplashApiKey) delete payload.unsplashApiKey;
      delete payload.hasGhostKey;
      delete payload.hasPexelsKey;
      delete payload.hasUnsplashKey;
      const saved = await saveChannel.mutateAsync(payload as never);
      setDraft(toDraft(saved));
      toast.success('Channel saved');
    } catch (error) {
      toast.error(error);
    }
  };

  const test = async () => {
    if (!draft?.id) {
      toast.error('Save the channel first');
      return;
    }
    try {
      const result = await testChannel.mutateAsync(draft.id);
      setTestResult(result);
      if (result.ghost.ok) toast.success('Ghost connection works');
      else toast.error(`Ghost failed: ${result.ghost.detail}`);
    } catch (error) {
      toast.error(error);
    }
  };

  if (isLoading) return <LoadingBlock />;

  return (
    <Page>
      <PageHeader
        title="Publishing"
        description="Where this project publishes blog posts. Ghost admin API for pushing drafts and published posts, with a house SEO bar and a publish window to pace things."
        actions={
          <>
            {channels && channels.length > 1 ? (
              <Select
                className="w-auto"
                value={draft?.id ?? ''}
                onChange={(event) => {
                  const found = channels.find((channel) => channel.id === event.target.value);
                  if (found) setDraft(toDraft(found));
                }}
              >
                {channels.map((channel) => (
                  <option key={channel.id} value={channel.id}>
                    {channel.label}
                  </option>
                ))}
              </Select>
            ) : null}
            <Button icon={<Plus className="h-4 w-4" />} onClick={() => setDraft({ ...BLANK })}>
              Add channel
            </Button>
            {draft?.id ? (
              <Button icon={<Plug className="h-4 w-4" />} loading={testChannel.isPending} onClick={test}>
                Test connection
              </Button>
            ) : null}
            <Button
              variant="primary"
              icon={<Save className="h-4 w-4" />}
              loading={saveChannel.isPending}
              onClick={save}
              disabled={!draft}
            >
              Save
            </Button>
          </>
        }
      />

      {session?.warnings.emailDryRun ? (
        <div className="mb-4 rounded-lg border border-warning/30 bg-warning/10 px-4 py-3 text-[13px] text-warning">
          <strong>Dry-run mode is on.</strong> Posts are drafted, scored and marked published, but
          nothing is pushed to a live site. Turn it off in{' '}
          <code className="font-mono">packages/backend/.env</code> when you are ready. The
          &ldquo;test connection&rdquo; button below ignores dry-run mode on purpose.
        </div>
      ) : null}

      {!draft ? (
        <Card>
          <EmptyState
            icon={<Globe className="h-5 w-5" />}
            title="No channel configured"
            description="Add one to publish posts. Nothing can be published until this exists."
            action={
              <Button variant="primary" onClick={() => setDraft({ ...BLANK })}>
                Add a channel
              </Button>
            }
          />
        </Card>
      ) : (
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="space-y-4 lg:col-span-2">
            <Card>
              <CardHeader title="Ghost connection" description="Admin API URL and admin key from your Ghost integration." />
              <CardBody className="grid gap-4 sm:grid-cols-2">
                <Field label="Label" htmlFor="cc-label" hint="For your own reference.">
                  <Input
                    id="cc-label"
                    value={draft.label}
                    onChange={(event) => setDraft({ ...draft, label: event.target.value })}
                  />
                </Field>
                <Field label="Site URL" htmlFor="cc-siteurl" hint="Public site, used to build post links.">
                  <Input
                    id="cc-siteurl"
                    value={draft.siteUrl}
                    placeholder="https://blog.rentx.example"
                    onChange={(event) => setDraft({ ...draft, siteUrl: event.target.value })}
                  />
                </Field>
                <Field label="Ghost admin URL" htmlFor="cc-adminurl" className="sm:col-span-2">
                  <Input
                    id="cc-adminurl"
                    value={draft.ghostAdminUrl}
                    placeholder="https://blog.rentx.example/ghost/api/admin"
                    onChange={(event) => setDraft({ ...draft, ghostAdminUrl: event.target.value })}
                  />
                </Field>
                <Field
                  label="Ghost admin key"
                  htmlFor="cc-adminkey"
                  className="sm:col-span-2"
                  hint={
                    draft.hasGhostKey
                      ? 'A key is stored. Encrypted at rest and never returned — leave empty to keep it.'
                      : 'From Ghost → Integrations → Admin API key. Encrypted at rest and never returned.'
                  }
                >
                  <Input
                    id="cc-adminkey"
                    type="password"
                    value={draft.ghostAdminKey}
                    placeholder={draft.hasGhostKey ? '•••••••• (stored)' : 'xxxxxxxx:yyyyyyyy'}
                    onChange={(event) => setDraft({ ...draft, ghostAdminKey: event.target.value })}
                  />
                </Field>
                <Field label="Default post status" htmlFor="cc-poststatus">
                  <Select
                    id="cc-poststatus"
                    value={draft.defaultPostStatus}
                    onChange={(event) =>
                      setDraft({ ...draft, defaultPostStatus: event.target.value as 'draft' | 'published' })
                    }
                  >
                    <option value="draft">Draft in Ghost</option>
                    <option value="published">Published in Ghost</option>
                  </Select>
                </Field>
                <Field label="Language" htmlFor="cc-language" hint="Default for posts on this channel.">
                  <Input
                    id="cc-language"
                    value={draft.language}
                    placeholder="en"
                    onChange={(event) => setDraft({ ...draft, language: event.target.value })}
                  />
                </Field>
                <Field
                  label="Embed snippet"
                  htmlFor="cc-embed"
                  className="sm:col-span-2"
                  hint="Optional HTML appended to every post — a CTA card, a signup form, a tracking pixel. Placeholders {{language}}, {{title}} and {{slug}} are filled in per post."
                >
                  <Textarea
                    id="cc-embed"
                    rows={3}
                    className="prompt-input"
                    value={draft.embedSnippet}
                    placeholder='<script src="https://rentx.example/embed.js" data-locale="{{language}}"></script>'
                    onChange={(event) => setDraft({ ...draft, embedSnippet: event.target.value })}
                  />
                </Field>
                <Field
                  label="Default tags"
                  htmlFor="cc-default-tags"
                  className="sm:col-span-2"
                  hint="Comma separated. Added to every post on publish, before the topic and post tags (deduplicated)."
                >
                  <Input
                    id="cc-default-tags"
                    value={draft.defaultTags}
                    placeholder="Albania, Travel"
                    onChange={(event) => setDraft({ ...draft, defaultTags: event.target.value })}
                  />
                </Field>

                <div className="sm:col-span-2 mt-1 border-t border-line pt-3">
                  <p className="mb-1 text-2xs font-semibold uppercase tracking-wide text-faint">
                    Images (optional)
                  </p>
                  <p className="mb-3 text-xs text-muted">
                    Add a Pexels and/or Unsplash API key to fetch a hero and per-section photos at publish
                    time. Without a key, posts publish without images. Encrypted at rest and never returned.
                  </p>
                </div>
                <Field
                  label="Pexels API key"
                  htmlFor="cc-pexels"
                  hint={draft.hasPexelsKey ? 'A key is stored — leave empty to keep it.' : 'From pexels.com/api.'}
                >
                  <Input
                    id="cc-pexels"
                    type="password"
                    value={draft.pexelsApiKey}
                    placeholder={draft.hasPexelsKey ? '•••••••• (stored)' : 'Pexels API key'}
                    onChange={(event) => setDraft({ ...draft, pexelsApiKey: event.target.value })}
                  />
                </Field>
                <Field
                  label="Unsplash access key"
                  htmlFor="cc-unsplash"
                  hint={
                    draft.hasUnsplashKey
                      ? 'A key is stored — leave empty to keep it.'
                      : 'The Access Key from unsplash.com/developers.'
                  }
                >
                  <Input
                    id="cc-unsplash"
                    type="password"
                    value={draft.unsplashApiKey}
                    placeholder={draft.hasUnsplashKey ? '•••••••• (stored)' : 'Unsplash access key'}
                    onChange={(event) => setDraft({ ...draft, unsplashApiKey: event.target.value })}
                  />
                </Field>
              </CardBody>
            </Card>
          </div>

          <div className="space-y-4">
            <Card>
              <CardHeader title="Publishing rules" description="The quality bar and the pace" />
              <CardBody className="space-y-4">
                <Field
                  label="Minimum SEO score"
                  htmlFor="cc-seo"
                  hint="Posts below this are held for review instead of auto-publishing."
                >
                  <Input
                    id="cc-seo"
                    type="number"
                    min={0}
                    max={100}
                    value={draft.minSeoScore}
                    onChange={(event) =>
                      setDraft({ ...draft, minSeoScore: Number(event.target.value) || 0 })
                    }
                  />
                </Field>

                <Field label="Posts per day" htmlFor="cc-daily">
                  <Input
                    id="cc-daily"
                    type="number"
                    min={0}
                    value={draft.dailyLimit}
                    onChange={(event) =>
                      setDraft({ ...draft, dailyLimit: Number(event.target.value) || 0 })
                    }
                  />
                </Field>

                <div className="grid grid-cols-2 gap-3">
                  <Field label="Window from" htmlFor="cc-start">
                    <Select
                      id="cc-start"
                      value={String(draft.publishWindowStart)}
                      onChange={(event) =>
                        setDraft({ ...draft, publishWindowStart: Number(event.target.value) })
                      }
                    >
                      {Array.from({ length: 24 }, (_, hour) => (
                        <option key={hour} value={hour}>
                          {String(hour).padStart(2, '0')}:00
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field label="Until" htmlFor="cc-end">
                    <Select
                      id="cc-end"
                      value={String(draft.publishWindowEnd)}
                      onChange={(event) =>
                        setDraft({ ...draft, publishWindowEnd: Number(event.target.value) })
                      }
                    >
                      {Array.from({ length: 24 }, (_, hour) => hour + 1).map((hour) => (
                        <option key={hour} value={hour}>
                          {String(hour).padStart(2, '0')}:00
                        </option>
                      ))}
                    </Select>
                  </Field>
                </div>

                <Field label="Timezone" htmlFor="cc-tz">
                  <Select
                    id="cc-tz"
                    value={draft.timezone}
                    onChange={(event) => setDraft({ ...draft, timezone: event.target.value })}
                  >
                    {TIMEZONES.map((zone) => (
                      <option key={zone} value={zone}>
                        {zone}
                      </option>
                    ))}
                  </Select>
                </Field>

                <div>
                  <span className="field-label">Publish days</span>
                  <div className="flex flex-wrap gap-1">
                    {DAYS.map(([value, label]) => {
                      const active = draft.publishDays.split(',').includes(value);
                      return (
                        <button
                          key={value}
                          type="button"
                          onClick={() => {
                            const days = new Set(draft.publishDays.split(',').filter(Boolean));
                            active ? days.delete(value) : days.add(value);
                            setDraft({
                              ...draft,
                              publishDays: Array.from(days).sort().join(',') || '1',
                            });
                          }}
                          className={`rounded-md border px-2 py-1 text-xs font-medium transition-colors ${
                            active
                              ? 'border-brand bg-brand text-brand-ink'
                              : 'border-line bg-surface text-muted hover:text-ink'
                          }`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-3 border-t border-line pt-3">
                  <Switch
                    checked={draft.isActive}
                    onChange={(value) => setDraft({ ...draft, isActive: value })}
                    label="Active"
                    description="Inactive channels are skipped by the publish worker."
                  />
                  <Switch
                    checked={draft.isDefault}
                    onChange={(value) => setDraft({ ...draft, isDefault: value })}
                    label="Default for this project"
                  />
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardHeader title="Test" description="Verify the Ghost credentials actually work" />
              <CardBody className="space-y-3">
                <Button
                  className="w-full"
                  icon={<Plug className="h-4 w-4" />}
                  loading={testChannel.isPending}
                  onClick={test}
                  disabled={!draft.id}
                >
                  Test connection
                </Button>

                {testResult ? (
                  <div className="space-y-1.5 rounded-lg border border-line bg-raised px-3 py-2.5 text-xs">
                    <TestRow label="Ghost" ok={testResult.ghost.ok} detail={testResult.ghost.detail} />
                  </div>
                ) : null}

                {draft.id ? (
                  <div className="space-y-1 border-t border-line pt-3 text-2xs text-faint">
                    {channels
                      ?.filter((channel) => channel.id === draft.id)
                      .map((channel) => (
                        <div key={channel.id} className="space-y-1">
                          <div>
                            Last publish:{' '}
                            {channel.lastPublishAt ? formatRelative(channel.lastPublishAt) : 'never'}
                          </div>
                          {channel.lastPublishError ? (
                            <div className="text-danger">Publish error: {channel.lastPublishError}</div>
                          ) : null}
                          <div className="flex gap-1.5 pt-1">
                            {channel.hasGhostKey ? (
                              <Badge tone="positive">Ghost key set</Badge>
                            ) : (
                              <Badge tone="warning">no Ghost key</Badge>
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                ) : null}

                {draft.id ? (
                  <Button
                    variant="ghost"
                    className="w-full"
                    icon={<Trash2 className="h-4 w-4" />}
                    onClick={() => {
                      const found = channels?.find((channel) => channel.id === draft.id);
                      if (found) setDeleteTarget(found);
                    }}
                  >
                    Delete channel
                  </Button>
                ) : null}
              </CardBody>
            </Card>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        title="Delete channel"
        destructive
        confirmLabel="Delete"
        loading={deleteChannel.isPending}
        message={
          <>
            Delete <strong className="text-ink">{deleteTarget?.label}</strong>? Posts published through
            it stay live on the site.
          </>
        }
        onConfirm={async () => {
          if (!deleteTarget) return;
          try {
            await deleteChannel.mutateAsync(deleteTarget.id);
            toast.success('Channel deleted');
            setDeleteTarget(null);
            setDraft(null);
          } catch (error) {
            toast.error(error);
          }
        }}
      />
    </Page>
  );
};

const TestRow = ({ label, ok, detail }: { label: string; ok: boolean; detail?: string }) => (
  <div className="flex items-start gap-2">
    {ok ? (
      <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-positive" />
    ) : (
      <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-danger" />
    )}
    <span className="min-w-0">
      <span className="font-medium text-ink">{label}</span>
      {detail ? <span className="ml-1 text-muted">— {detail}</span> : null}
    </span>
  </div>
);

const toDraft = (channel: ContentChannel): Draft => ({
  id: channel.id,
  label: channel.label,
  provider: channel.provider,
  ghostAdminUrl: channel.ghostAdminUrl ?? '',
  ghostAdminKey: '',
  pexelsApiKey: '',
  unsplashApiKey: '',
  hasGhostKey: channel.hasGhostKey,
  hasPexelsKey: channel.hasPexelsKey,
  hasUnsplashKey: channel.hasUnsplashKey,
  siteUrl: channel.siteUrl ?? '',
  defaultPostStatus: channel.defaultPostStatus,
  embedSnippet: channel.embedSnippet ?? '',
  defaultTags: channel.defaultTags ?? '',
  minSeoScore: channel.minSeoScore,
  language: channel.language ?? '',
  dailyLimit: channel.dailyLimit,
  publishWindowStart: channel.publishWindowStart,
  publishWindowEnd: channel.publishWindowEnd,
  publishDays: channel.publishDays,
  timezone: channel.timezone,
  isDefault: channel.isDefault,
  isActive: channel.isActive,
});
